import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSportConfig } from '../context/SportConfig';
import '../styles/MyTeams.css';

function MyTeams() {
  const { token, user, logout } = useAuth();
  const sportConfig = useSportConfig();
  const navTitle = sportConfig ? `${sportConfig.appEmoji} ${sportConfig.appName}` : '…';
  const [leagues, setLeagues] = useState([]);
  const [teamsData, setTeamsData] = useState({});
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    fetchLeaguesAndTeams();
  }, []);

  const fetchLeaguesAndTeams = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/leagues`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.success) return;

      setLeagues(data.leagues);

      // Fetch draft picks and standings for each league in parallel
      const teamResults = {};
      await Promise.all(
        data.leagues.map(async (league) => {
          try {
            const [picksRes, standingsRes] = await Promise.all([
              fetch(`${API_URL}/draft/${league._id}`, {
                headers: { Authorization: `Bearer ${token}` },
              }),
              fetch(`${API_URL}/scores/${league._id}/standings`, {
                headers: { Authorization: `Bearer ${token}` },
              }),
            ]);
            const picksData = await picksRes.json();
            const standingsData = await standingsRes.json();

            const myPicks = picksData.success
              ? picksData.picks.filter(p => p.manager._id === user._id)
              : [];
            const myStanding = standingsData.success
              ? standingsData.standings.find(s => s.manager._id === user._id)
              : null;
            const rank = standingsData.success
              ? standingsData.standings.findIndex(s => s.manager._id === user._id) + 1
              : null;

            teamResults[league._id] = { picks: myPicks, standing: myStanding, rank };
          } catch {
            teamResults[league._id] = { picks: [], standing: null, rank: null };
          }
        })
      );

      setTeamsData(teamResults);
    } catch {
      // leagues will just be empty
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="myteams-container">
      <nav className="dashboard-nav">
        <div className="nav-left">
          <h1 className="nav-title">{navTitle}</h1>
        </div>
        <div className="nav-right">
          <Link to="/dashboard" className="btn btn-secondary">Dashboard</Link>
          <Link to="/leagues" className="btn btn-secondary">Leagues</Link>
          <button onClick={logout} className="btn btn-logout">Logout</button>
        </div>
      </nav>

      <div className="myteams-content">
        <header className="myteams-header">
          <h2>👥 My Teams</h2>
          <p>Your drafted rosters across all leagues</p>
        </header>

        {loading && <p className="status-msg">Loading your teams...</p>}

        {!loading && leagues.length === 0 && (
          <div className="empty-state">
            <p>You haven't joined any leagues yet.</p>
            <Link to="/leagues" className="btn btn-primary">Browse Leagues</Link>
          </div>
        )}

        {!loading && leagues.map(league => {
          const team = teamsData[league._id] || { picks: [], standing: null, rank: null };
          const budget = league.draftBudget || 175;
          const spent = team.picks.reduce((sum, p) => sum + p.costCredits, 0);

          return (
            <div key={league._id} className="team-card">
              <div className="team-card-header">
                <div>
                  <Link to={`/leagues/${league._id}`} className="team-league-name">{league.name}</Link>
                  <span className="team-tournament">{league.tournament?.name}</span>
                </div>
                <div className="team-card-stats">
                  {team.rank && (
                    <span className="team-rank">#{team.rank}</span>
                  )}
                  {team.standing && (
                    <span className="team-points">{team.standing.totalPoints} pts</span>
                  )}
                  <span className={`draft-status-badge ${league.draftStatus}`}>
                    {league.draftStatus}
                  </span>
                </div>
              </div>

              {team.picks.length > 0 ? (
                <div className="team-roster">
                  <div className="roster-summary">
                    <span>🎯 {team.picks.length} players</span>
                    <span>💰 {spent} / {budget} credits spent</span>
                  </div>
                  <table className="roster-table">
                    <thead>
                      <tr>
                        <th>Player</th>
                        <th>Tour</th>
                        <th>Rank</th>
                        <th>Cost</th>
                        {team.standing && <th>Pts</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {[...team.picks]
                        .sort((a, b) => {
                          const aScore = team.standing?.players.find(p => p.player._id === a.player._id);
                          const bScore = team.standing?.players.find(p => p.player._id === b.player._id);
                          const aOut = (aScore?.roundsLost || []).length > 0;
                          const bOut = (bScore?.roundsLost || []).length > 0;
                          if (aOut !== bOut) return aOut ? 1 : -1;
                          return (bScore?.points || 0) - (aScore?.points || 0);
                        })
                        .map(pick => {
                          const playerScore = team.standing
                            ? team.standing.players.find(p => p.player._id === pick.player._id)
                            : null;
                          const isEliminated = (playerScore?.roundsLost || []).length > 0;
                          return (
                            <tr key={pick._id} className={isEliminated ? 'player-eliminated-row' : ''}>
                              <td className="player-name">
                                <a href={`https://google.com/search?q=${encodeURIComponent(pick.player.name)}`} target="_blank" rel="noopener noreferrer" className={isEliminated ? 'player-eliminated' : ''}>{pick.player.name}</a>
                                {isEliminated && <span className="eliminated-badge">❌</span>}
                              </td>
                              <td>
                                <span className={`tour-badge ${pick.player.tour.toLowerCase()}`}>
                                  {pick.player.tour}
                                </span>
                              </td>
                              <td>#{pick.player.ranking}</td>
                              <td>{pick.costCredits} cr</td>
                              {team.standing && (
                                <td className="player-pts">
                                  {playerScore ? playerScore.points : 0}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="team-empty">
                  <p>No players drafted yet.</p>
                  {league.draftStatus === 'drafting' && (
                    <Link to={`/leagues/${league._id}/draft`} className="btn btn-small btn-primary">
                      Draft Players
                    </Link>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MyTeams;
